import { useState, useEffect, useCallback } from 'react';
import { revealHiddenChannels } from '@ego/api';
import { getImageUrl } from '@ego/api';
import { relativeTime } from '@ego/domain';
import useAuth from './useAuth';

const STORAGE_KEY_PREFIX = 'hidden-revealed-';
const TOKEN_KEY_PREFIX = 'channel-unlock-token-';

export default function useRevealHidden(handle) {
  const { session } = useAuth();
  const [revealedChannels, setRevealedChannels] = useState([]);

  // Restore revealed channel IDs from sessionStorage on mount
  useEffect(() => {
    if (!handle) return;
    const stored = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${handle}`);
    if (stored) {
      try {
        setRevealedChannels(JSON.parse(stored));
      } catch {
        // Corrupt data, clear it
        sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${handle}`);
      }
    }
  }, [handle]);

  const revealHidden = useCallback(async (password) => {
    if (!handle) return [];

    const channels = await revealHiddenChannels(handle, password, session?.access_token);
    if (!channels.length) return [];

    // Store unlock tokens in sessionStorage
    for (const ch of channels) {
      sessionStorage.setItem(`${TOKEN_KEY_PREFIX}${handle}-${ch.slug}`, ch.unlockToken);
    }

    // Build display-ready channel objects
    const displayChannels = channels.map(ch => ({
      id: ch.id,
      title: ch.title,
      slug: ch.slug,
      handle: ch.handle,
      author: ch.author,
      accentColor: ch.accentColor,
      borderColor: ch.borderColor,
      blocksCount: ch.blocksCount,
      updatedRelative: relativeTime(ch.updatedAt),
      visibility: ch.visibility,
      hasPassword: ch.hasPassword,
      previews: (ch.previews || []).map(p =>
        p ? {
          id: p.id,
          type: p.type,
          title: p.title,
          content: p.content,
          imageUrl: getImageUrl(p.imagePath),
          createdAt: p.createdAt,
          channelId: p.channelId,
        } : null,
      ),
    }));

    // Store in sessionStorage and state
    sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${handle}`, JSON.stringify(displayChannels));
    setRevealedChannels(displayChannels);

    return displayChannels;
  }, [handle, session?.access_token]);

  const clearRevealed = useCallback(() => {
    if (!handle) return;
    // Clear all tokens for revealed channels
    for (const ch of revealedChannels) {
      sessionStorage.removeItem(`${TOKEN_KEY_PREFIX}${handle}-${ch.slug}`);
    }
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${handle}`);
    setRevealedChannels([]);
  }, [handle, revealedChannels]);

  return { revealedChannels, revealHidden, clearRevealed };
}
